import { ADOBE_FONT } from '@components/Theme/constants';
import { useFullstoryElement } from '@hooks/useFullstory';
import { useTheme } from '@hooks/useTheme';
import { createCalendar } from '@internationalized/date';
import { t } from '@lingui/core/macro';
import { useDateFieldState } from '@react-stately/datepicker';
import { FullStoryElements, FullStoryElementType } from '@utils/fullstory';
import { useWorkflow } from '@utils/fullstory/context';
import { FC, useRef } from 'react';
import { useDateField, useDateSegment, useLocale } from 'react-aria';
import { useMount } from 'react-use';
import { DateType } from '../defs';

interface DateSegmentProps {
  segment: anyOk;
  state: anyOk;
  isHidden?: boolean;
  fsName?: string;
  fsParent?: string;
  fsElement?: FullStoryElementType;
}

const DateSegment: FC<DateSegmentProps> = ({
  segment,
  state,
  isHidden = false,
  fsElement = FullStoryElements.FIELD_INPUT,
  fsParent,
  fsName,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useMount(() => {
    const yearSegment = state.segments.find((s: fixMe) => s.type === 'year');
    // this is to ensure that the year segment is always filled with a valid year
    // when the date field is mounted, especially for cases where the user might not have selected a year yet
    // and the segment is still in placeholder state
    if (yearSegment?.text == 'yyyy' || yearSegment?.isPlaceholder) {
      state.setSegment('year', new Date().getFullYear().toString());
    }
  });
  const { segmentProps } = useDateSegment(segment, state, ref);
  const {
    colors: { primary, textReverse, disabledText },
  } = useTheme();
  const { getFsComponentProps } = useFullstoryElement();
  const { id } = useWorkflow();
  const isSlash = segment.type === 'literal';

  // as of writing, leading zeroes were not found to be supported natively by react-aria
  // add them here, only for non-literal segments
  const segmentText =
    segment.type !== 'literal' ? segment.text.padStart(2, '0') : segment.text;
  return (
    <div
      {...segmentProps}
      ref={ref}
      css={{
        padding: 2,
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'end',
        fontSize: 14,
        minWidth: isSlash ? undefined : '2.6ch',
        color: segment.isPlaceholder || isSlash ? disabledText : undefined,
        '&:focus': {
          background: primary,
          color: textReverse,
          borderRadius: 3,
        },
      }}
      hidden={isHidden}
      {...getFsComponentProps({
        name: fsName ?? 'date-picker',
        parent: fsParent ?? id,
        element: fsElement,
        type: 'date-picker',
      })}
    >
      {segmentText}
    </div>
  );
};

interface Props {
  onChange: (val: DateType) => void;
  value?: Maybe<DateType> | null;
  singleDate?: boolean;
  showYears?: boolean;
  nonClearable?: boolean;
  fsName?: string;
  fsParent?: string;
  fsSearchElement?: FullStoryElementType;
}

export const DateField: FC<Props> = (props) => {
  const { locale } = useLocale();

  const state = useDateFieldState({
    ...(props as fixMe),
    granularity: 'day',
    locale,
    createCalendar: createCalendar as fixMe,
  });
  const ref = useRef<HTMLDivElement>(null);

  const { fieldProps } = useDateField(
    {
      ...props,
      'aria-label': t`Date`,
      granularity: 'day',
      hideTimeZone: true,
    },
    state as fixMe,
    ref as anyOk,
  );

  // When the year is hidden, the separator attached to it would otherwise
  // render as a dangling slash, so hide that separator too.
  const yearSegmentIndex = state.segments.findIndex(
    (s: fixMe) => s.type === 'year',
  );
  const danglingSeparatorIndex =
    yearSegmentIndex <= 0 ? yearSegmentIndex + 1 : yearSegmentIndex - 1;
  const isSegmentHidden = (segment: fixMe, index: number): boolean =>
    !props?.showYears &&
    (segment.type === 'year' ||
      (segment.type === 'literal' && index === danglingSeparatorIndex));

  return (
    <div ref={ref}>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          fontFamily: ADOBE_FONT,
        }}
      >
        <div
          {...fieldProps}
          css={
            props?.singleDate
              ? {
                  display: 'inline-flex',
                  padding: '2px 4px',
                  borderRadius: '2px',
                  width: '100%',
                }
              : {
                  display: 'inline-flex',
                  padding: '2px 4px',
                  borderRadius: '2px',
                }
          }
        >
          {props?.singleDate ? (
            <span css={{ display: 'flex' }}>
              {state.segments.map((segment, i) => (
                <DateSegment
                  key={i}
                  segment={segment}
                  state={state}
                  isHidden={isSegmentHidden(segment, i)}
                  fsElement={props?.fsSearchElement}
                  fsParent={props?.fsParent}
                  fsName={props?.fsName}
                />
              ))}
            </span>
          ) : (
            state.segments.map((segment, i) => {
              return (
                <DateSegment
                  key={i}
                  segment={segment}
                  state={state}
                  isHidden={isSegmentHidden(segment, i)}
                  fsElement={props?.fsSearchElement}
                  fsParent={props?.fsParent}
                  fsName={props?.fsName}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
